import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import {useStylesECDS} from './ecdsStyle';
import config from '../../../../app/config';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import {AgyleTable, createAgyleTableColumn} from '../../../../components/table/AgyleTable';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import _ from 'lodash';
import useOpenState from '../../../../hooks/useOpenState';
import uuidv4 from 'uuid/v4';
import moment from 'moment/moment';
import MessageBox from '../../../../util/messageBox';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import {Divider} from '@mui/material';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import {calculateArrivalDate} from '../../../../util/patientUtil';

const COLUMNS = [
    createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('startDate', 'Start Date', 2, 'StartDate_CommissionerAssignmentPeriod', 'string', '', 'medium', 'left', true, true, null, null, null),
    createAgyleTableColumn('endDate', 'End Date', 3, 'EndDate_CommissionerAssignmentPeriod', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('codeOfCommissioner', 'Code of Commissioner', 4, 'OrganisationIdentifier_CodeOfCommissioner', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('CommissionerReferenceIdentifier', 'Commissioner Reference Identifier', 5, 'CommissionerReferenceIdentifier', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('agreementIdentifier', 'Agreement Identifier', 6, 'NHSServiceAgreementIdentifier', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('agreementLineIdentifier', 'Agreement Line Identifier', 7, 'NHSServiceAgreementLineIdentifier', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('providerReferenceIdentifier', 'Provider Reference Identifier', 8, 'ProviderReferenceIdentifier', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('serviceCode', 'Service Code', 9, 'ServiceCode', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('action', 'Action', 10, null, 'action', 'notSort', 'medium', 'left', true, true, null, null, null),
];

const AgreementServiceDetailsV4 = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();

    const {isSmall} = props;

    // Agreement Service Details Dataset
    const [codeOfProvider, setCodeOfProvider] = useState('');
    const [form, setForm] = useState({
        codeOfCommissioner: '',
        startDate: null,
        endDate: null,
        agreementIdentifier: '',
        agreementLineIdentifier: '',
        providerReferenceIdentifier: '',
        commissionerReferenceIdentifier: '',
        serviceCode: '',
    });
    const [list, setList] = useState([]);

    // Validation
    const validationEnabled = config?.modules?.forms?.ecds?.validation || false;
    const [error, setError] = useState({
        startDate: false,
        endDate: false,
    });
    const errorPicker = useRef({
        startDate: null,
        endDate: null,
    });

    // Accordion
    const [expanded, setExpanded] = React.useState('');

    //  For Delete
    const [deleteItem, setDeleteItem] = useState(null);
    const [isWarningOpen, openWarning, closeWarning] = useOpenState(false, false);

    // Initial Data
    useEffect(() => {
        if (props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.ServiceAgreementDetails) {
            const data = props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.ServiceAgreementDetails;
            setCodeOfProvider(data?.OrganisationIdentifier_CodeOfProvider || '');
            setList(data?.Commissioners || []);
        }
    }, []);

    const updateData = () => {
        const data = {
            OrganisationIdentifier_CodeOfProvider: codeOfProvider || '',
            Commissioners: list || [],
        };
        props.setData(data);
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            updateData();
        }, 400);

        return () => clearTimeout(timeoutId);
    }, [codeOfProvider, list]);

    const changeHandler = (event) => {
        const {name, value} = event.target;
        setForm((prevState) => ({...prevState, [name]: value}));
    };

    const addToList = () => {
        if ((form.startDate ? moment(form.startDate).isValid() : true) && (form.endDate ? moment(form.endDate).isValid() : true) && form.codeOfCommissioner) {
            let data = {
                id: uuidv4() + '',
                OrganisationIdentifier_CodeOfCommissioner: form.codeOfCommissioner,
                StartDate_CommissionerAssignmentPeriod: moment(form.startDate).format(config.ecdsDateFormat),
                EndDate_CommissionerAssignmentPeriod: moment(form.endDate).format(config.ecdsDateFormat),
                NHSServiceAgreementIdentifier: form.agreementIdentifier,
                NHSServiceAgreementLineIdentifier: form.agreementLineIdentifier,
                ProviderReferenceIdentifier: form.providerReferenceIdentifier,
                CommissionerReferenceIdentifier: form.commissionerReferenceIdentifier,
                ServiceCode: form.serviceCode,
            };
            setList((oldArray) => [...oldArray, data]);
            clearData();
        } else {
            setError({
                startDate: form.startDate ? !moment(form.startDate).isValid() : false,
                endDate: form.endDate ? !moment(form.endDate).isValid() : false,
                codeOfCommissioner: _.isEmpty(form.codeOfCommissioner),
            });
        }
    };

    const clearData = () => {
        const clearDataForm = {
            codeOfCommissioner: '',
            startDate: null,
            endDate: null,
            agreementIdentifier: '',
            agreementLineIdentifier: '',
            providerReferenceIdentifier: '',
            commissionerReferenceIdentifier: '',
            serviceCode: '',
        };
        setForm(clearDataForm);
        setError({
            codeOfProvider: validationEnabled,
            codeOfCommissioner: validationEnabled,
            startDate: false,
            endDate: false,
        });
    };

    const handleAccordionChange = (panel) => (event, newExpanded) => setExpanded(newExpanded ? panel : false);

    const deleteReferral = () => {
        setList(prevState => prevState.filter(value => value.id !== deleteItem.id));
        setDeleteItem(null);
        closeWarning();
        clearData();
    };

    const handleDeleteItem = (item) => {
        setDeleteItem(item);
        openWarning();
    };

    const renderTable = () => {
        if (list) {
            return (
                <>
                    <AgyleAccordion
                        expanded={expanded === 'mental'}
                        onChange={handleAccordionChange('mental')}
                        label={t('Table')}
                    >
                        <AgyleTable
                            columns={COLUMNS}
                            data={list}
                            deleteItem={handleDeleteItem}
                        />
                    </AgyleAccordion>
                </>
            );
        }
    };

    if (props.accordion !== 'Service Agreement Details') return null;

    return (
        <div className={classes.root}>
            {!_.isEmpty(list) && renderTable()}

            <Grid container spacing={2} direction="row" style={{marginTop: 1}}>
                <Grid item xs={12}>
                    <AgyleTextField
                        label={t('Organisation Identifier (Code of Provider)')}
                        onChange={e => setCodeOfProvider(e.target.value)}
                        value={codeOfProvider || ''}
                        error={_.isEmpty(codeOfProvider)}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Divider sx={{m: 1, width: '100%'}}/>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        label={t('Start Date (Commissioner Assignment Period)')}
                        value={form.startDate}
                        onChange={e => {
                            setForm(prevState => ({...prevState, startDate: e}));
                            setError(prevState => ({...prevState, startDate: false}));
                        }}
                        type="date"
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        onError={event => errorPicker.current = {
                            ...errorPicker.current,
                            startDate: event,
                        }}
                        error={error.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        label={t('End Date (Commissioner Assignment Period)')}
                        value={form.endDate}
                        onChange={e => {
                            setForm(prevState => ({...prevState, endDate: e}));
                            setError(prevState => ({...prevState, endDate: false}));
                        }}
                        type="date"
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        onError={event => errorPicker.current = {
                            ...errorPicker.current,
                            endDate: event,
                        }}
                        error={error.endDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="codeOfCommissioner"
                        label={t('Organisation Identifier (Code of Commissioner)')}
                        onChange={changeHandler}
                        value={form.codeOfCommissioner || ''}
                        error={error.codeOfCommissioner}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="commissionerReferenceIdentifier"
                        label={t('Commissioner Reference Identifier')}
                        onChange={changeHandler}
                        value={form.commissionerReferenceIdentifier || ''}
                        error={error.commissionerReferenceIdentifier}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="agreementIdentifier"
                        label={t('NHS Service Agreement Identifier')}
                        onChange={changeHandler}
                        value={form.agreementIdentifier || ''}
                        error={error.agreementIdentifier}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="agreementLineIdentifier"
                        label={t('NHS Service Agreement Line Identifier')}
                        onChange={changeHandler}
                        value={form.agreementLineIdentifier || ''}
                        error={error.agreementLineIdentifier}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="providerReferenceIdentifier"
                        label={t('Provider Reference Identifier')}
                        onChange={changeHandler}
                        value={form.providerReferenceIdentifier || ''}
                        error={error.providerReferenceIdentifier}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="serviceCode"
                        label={t('Service Code')}
                        onChange={changeHandler}
                        value={form.serviceCode || ''}
                        error={error.serviceCode}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleButton
                        variant="contained"
                        onClick={addToList}
                        style={{marginLeft: 'auto'}}
                    >
                        {t('Add Record')}
                    </AgyleButton>
                </Grid>
            </Grid>

            <MessageBox
                open={isWarningOpen}
                onClose={closeWarning}
                title={t('Confirmation')}
                message={t('Do you really want to remove this item?')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={deleteReferral}
                warning
            />
        </div>
    );
};

export default AgreementServiceDetailsV4;
